/**
 * /platform/users — platform-admin tool for cascade-deleting any user.
 *
 *  1. Look up by email → see profile + everything that would be deleted.
 *  2. Type the email again to confirm.
 *  3. One-click purge → backend cascade across USER_OWNED_COLLECTIONS +
 *     email-indexed audit tables, then deletes the users row.
 *  4. Recent purges visible at the bottom for traceability.
 */
import React, { useEffect, useState } from 'react';
import { Search, AlertTriangle, Trash2, ShieldAlert, History, RotateCw } from 'lucide-react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';

function PreviewTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-fg-muted">Nothing else to delete — this is an orphan account.</p>;
  }
  return (
    <div className="border border-soft rounded-lg overflow-hidden">
      <table className="w-full text-sm" data-testid="purge-preview-table">
        <thead className="bg-fg/5 text-left text-xs uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2">Collection</th>
            <th className="px-3 py-2">Match field</th>
            <th className="px-3 py-2 text-right">Records</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-soft">
              <td className="px-3 py-1.5 font-mono text-xs">{r.collection}</td>
              <td className="px-3 py-1.5 font-mono text-xs text-fg-muted">{r.field}</td>
              <td className="px-3 py-1.5 text-right font-semibold">{r.count ?? r.deleted}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryCard() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = () => {
    setBusy(true);
    api.get('/platform/users/purge-history?limit=50')
      .then(r => setRows(r.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setBusy(false));
  };
  useEffect(() => { reload(); }, []);
  return (
    <div className="bento-card mt-8" data-testid="purge-history-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-bold inline-flex items-center gap-2"><History className="w-4 h-4 text-brand"/> Recent purges</h2>
        <button onClick={reload} disabled={busy} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="purge-history-refresh">
          <RotateCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`}/> Refresh
        </button>
      </div>
      {rows === null ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-fg-muted">No purges have been recorded yet.</p>
      ) : (
        <div className="space-y-2 max-h-80 overflow-auto">
          {rows.map((r, i) => (
            <div key={i} className="border border-soft rounded-md p-3 text-xs" data-testid={`purge-history-row-${i}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <div className="font-mono break-all">{r.target_email}</div>
                  <div className="text-fg-muted">{new Date(r.created_at).toLocaleString()} · by {r.actor_email}</div>
                  {r.reason && <div className="text-fg-secondary mt-1">Reason: {r.reason}</div>}
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="font-semibold">{(r.user_doc_removed || 0) + (r.cascade_total || 0)} docs</div>
                  <div className="text-fg-muted">{r.target_role || '(no user row)'}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PlatformPurgeUser() {
  const [email, setEmail] = useState('');
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [historyKey, setHistoryKey] = useState(0); // remount HistoryCard after purge

  const doLookup = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setLoading(true); setErr(''); setResult(null); setLookup(null); setConfirm('');
    try {
      const { data } = await api.get('/platform/users/lookup', { params: { email: email.trim() } });
      setLookup(data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message);
    } finally { setLoading(false); }
  };

  const doPurge = async () => {
    if (confirm.trim().toLowerCase() !== email.trim().toLowerCase()) {
      setErr('Confirm email must match exactly.'); return;
    }
    if (!await safeConfirm(`Permanently delete ${email} and ALL associated records? This cannot be undone.`)) return;
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/platform/users/purge', {
        email: email.trim(),
        confirm_email: confirm.trim(),
        reason: reason.trim() || null,
      });
      setResult(data);
      setLookup(null); setConfirm(''); setReason('');
      setHistoryKey(k => k + 1);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#B83A3A] font-medium mb-2 inline-flex items-center gap-1">
          <ShieldAlert className="w-3 h-3"/> DESTRUCTIVE
        </div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Purge user</h1>
        <p className="text-fg-muted text-sm mt-1">
          Look up any account by email and cascade-delete every record tied to it.
          Use this for orphan support tickets and GDPR Article 17 requests forwarded by reception.
        </p>
      </div>

      {/* Step 1: Lookup */}
      <form onSubmit={doLookup} className="bento-card" data-testid="purge-lookup-card">
        <h2 className="font-heading font-bold mb-2 inline-flex items-center gap-2"><Search className="w-4 h-4 text-brand"/> 1. Look up by email</h2>
        <div className="flex gap-2 flex-wrap">
          <input
            type="email"
            value={email}
            onChange={e=>setEmail(e.target.value)}
            placeholder="patient@example.com"
            className="input flex-1 min-w-[260px]"
            data-testid="purge-email-input"
            autoComplete="off"
            required
          />
          <button type="submit" disabled={loading || !email.trim()} className="btn-primary inline-flex items-center gap-2" data-testid="purge-lookup-btn">
            <Search className="w-4 h-4"/> {loading ? 'Looking up…' : 'Look up'}
          </button>
        </div>
        {err && (
          <div className="mt-3 text-sm text-[#B83A3A] inline-flex items-center gap-2" data-testid="purge-error">
            <AlertTriangle className="w-4 h-4"/> {err}
          </div>
        )}
      </form>

      {/* Step 2: Preview */}
      {lookup && (
        <div className="bento-card mt-6" data-testid="purge-preview-card">
          <h2 className="font-heading font-bold mb-3 inline-flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FFB000]"/> 2. Preview cascade</h2>

          {!lookup.user ? (
            <p className="text-sm text-fg-muted" data-testid="purge-not-found">
              No user row exists for <strong>{email}</strong>. Nothing will be removed by clicking purge.
            </p>
          ) : (
            <div className="space-y-3 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="purge-user-summary">
                <div><div className="text-[10px] uppercase tracking-wider text-fg-muted">Email</div><div className="font-mono break-all">{lookup.user.email}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-fg-muted">Role</div><div className="capitalize">{lookup.user.role}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-fg-muted">Clinic</div><div className="font-mono text-xs">{lookup.user.clinic_id || '—'}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-fg-muted">Created</div><div className="text-xs">{lookup.user.created_at?.slice(0,10)}</div></div>
              </div>

              {lookup.owns_clinic && (
                <div className="bg-red-50 border-2 border-red-300 rounded p-3 text-sm" data-testid="purge-owns-clinic-warning">
                  <div className="font-semibold text-red-800 inline-flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4"/> This account is the OWNER of an active clinic.
                  </div>
                  <div className="mt-1 text-red-900 text-xs">
                    Clinic <strong>{lookup.owns_clinic.name}</strong> ({lookup.owns_clinic.slug}) — plan {lookup.owns_clinic.plan},
                    status {lookup.owns_clinic.subscription_status}.
                    Purging will NOT delete the clinic doc itself, just this owner user.
                    Re-assign ownership first if other staff still rely on this clinic.
                  </div>
                </div>
              )}
            </div>
          )}

          <PreviewTable rows={lookup.preview?.rows}/>
          <div className="mt-3 text-sm">
            <strong>Total documents to delete:</strong>{' '}
            <span className="text-[#B83A3A] font-semibold" data-testid="purge-preview-total">
              {(lookup.preview?.total || 0) + (lookup.would_delete_users_row ? 1 : 0)}
            </span>
            {lookup.would_delete_users_row && ' (incl. the user record itself)'}
          </div>
        </div>
      )}

      {/* Step 3: Confirm + execute */}
      {lookup && (lookup.user || lookup.preview?.total > 0) && (
        <div className="bento-card mt-6 border-2 border-red-300" data-testid="purge-confirm-card">
          <h2 className="font-heading font-bold mb-3 inline-flex items-center gap-2 text-red-800">
            <Trash2 className="w-4 h-4"/> 3. Confirm and purge
          </h2>
          <p className="text-sm text-fg-muted mb-3">
            Re-type the email exactly. The cascade runs in one shot and writes a single
            audit row tagged with your platform-admin user.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <input
              type="email"
              value={confirm}
              onChange={e=>setConfirm(e.target.value)}
              placeholder={`Re-type: ${email}`}
              className="input"
              data-testid="purge-confirm-input"
              autoComplete="off"
            />
            <input
              type="text"
              value={reason}
              onChange={e=>setReason(e.target.value)}
              placeholder="Reason (optional, written to audit log)"
              className="input"
              data-testid="purge-reason-input"
            />
          </div>
          <div className="mt-4">
            <button
              onClick={doPurge}
              disabled={busy || confirm.trim().toLowerCase() !== email.trim().toLowerCase()}
              className="px-4 py-2 bg-[#B83A3A] text-white rounded-md font-semibold text-sm hover:bg-[#9C2A2A] disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="purge-execute-btn"
            >
              <Trash2 className="w-4 h-4"/> {busy ? 'Purging…' : 'Cascade purge — irreversible'}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="bento-card mt-6 border-2 border-green-300 bg-green-50/30" data-testid="purge-result-card">
          <h2 className="font-heading font-bold mb-2 inline-flex items-center gap-2 text-green-800">
            <Trash2 className="w-4 h-4"/> Purge complete
          </h2>
          <p className="text-sm text-fg-muted mb-3">
            Removed <strong className="text-fg" data-testid="purge-result-total">{result.total_documents_removed}</strong> document(s) total —
            cascade: {result.cascade_total}, user row: {result.user_doc_removed}.
          </p>
          {result.deleted && result.deleted.length > 0 && <PreviewTable rows={result.deleted}/>}
        </div>
      )}

      <HistoryCard key={historyKey}/>
    </div>
  );
}
